// react
import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// react-aria
import { mergeProps, useButton, useFocusRing } from 'react-aria';

// icons
import { Icon, Icons } from '@/components/atoms/Icon';

// assets
// @transform-path ../../../../../../src/assets/social/google-icon.svg
import googleIcon from '@/assets/social/google-icon.svg';

/**
 * ButtonSocial allows the user to trigger an action or event, such as submitting a
 * form, opening a dialog, canceling an action, or performing a delete
 * operation.
 */
export const ButtonSocial = ({
  className,
  isDisabled = false,
  isInline = false,
  isFullWidth = false,
  type = 'button',
  ariaLabel,
  children,
  to,
  variant = 'facebook',
  ...props
}: ButtonSocialProps) => {
  // hooks
  const ref = useRef<HTMLButtonElement>(null);
  const { isFocusVisible, focusProps } = useFocusRing();
  const { buttonProps, isPressed } = useButton(
    { ...props, isDisabled, type },
    ref,
  );

  return (
    <StyledButtonSocial
      {...mergeProps(buttonProps, focusProps)}
      {...{ to: to }}
      className={className}
      ref={ref}
      aria-label={ariaLabel}
      $isFocusVisible={isFocusVisible}
      $isPressed={isPressed}
      $isInline={isInline}
      $isFullWidth={isFullWidth}
      $variant={variant}
      as={to ? Link : 'button'}
    >
      {socialModel[variant].customIcon && (
        <StyledImg src={socialModel[variant].customIcon} />
      )}
      {socialModel[variant].icon && (
        <StyledIcon
          icon={socialModel[variant].icon || 'star'}
          type={socialModel[variant].iconType || 'fab'}
          fontSize={24}
        />
      )}
      <StyledButtonSocialLabel>
        {children || socialModel[variant].defaultLabel}
      </StyledButtonSocialLabel>
    </StyledButtonSocial>
  );
};

interface StyledButtonSocialProps {
  $isFocusVisible?: boolean;
  $isPressed?: boolean;
  $isInline?: boolean;
  $isFullWidth?: boolean;
  $variant: Social;
}
const StyledButtonSocial = styled.button<StyledButtonSocialProps>`
  display: ${(props) => (props.$isInline ? 'inline-flex' : 'flex')};
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[12]};

  border: none;
  box-sizing: border-box;
  position: relative;
  cursor: pointer;
  user-select: none;
  outline: none;

  font: ${(props) => props.theme.typography.button.md};
  text-decoration: none;

  ${(props) => {
    if (socialModel[props.$variant].isInvert) {
      return css`
        background: ${socialModel[props.$variant].color};
        color: ${(props) => props.theme.color.neutral[10]};
      `;
    } else {
      return css`
        background: ${(props) => props.theme.color.neutral[10]};
        border: 1px solid ${socialModel[props.$variant].color};
        color: ${socialModel[props.$variant].color};
      `;
    }
  }}

  padding: ${(props) => props.theme.spacing[12]}
    ${(props) => props.theme.spacing[16]};

  border-radius: 99999px;

  ${(props) =>
    props.$isFullWidth &&
    css`
      width: 100%;
    `}

  &:disabled {
    pointer-events: none;
  }
`;

const StyledButtonSocialLabel = styled.div`
  white-space: nowrap;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  left: ${(props) => props.theme.spacing[16]};
  transform: translateY(-50%);
  width: 24px;
  display: flex;
  align-items: center;
`;

const StyledImg = styled.img`
  position: absolute;
  top: 50%;
  left: ${(props) => props.theme.spacing[16]};
  transform: translateY(-50%);
`;

type Social = 'apple' | 'google' | 'facebook' | 'email';

const socialModel: Record<
  Social,
  {
    icon?: Icons;
    iconType?: 'fal' | 'fab';
    customIcon?: string;
    color: string;
    isInvert?: boolean;
    defaultLabel?: string;
  }
> = {
  apple: {
    icon: 'apple',
    color: '#000000',
    defaultLabel: 'Sign in with Apple',
    isInvert: true,
  },
  google: {
    customIcon: googleIcon,
    color: '#000000',
    defaultLabel: 'Sign in with Google',
  },
  facebook: {
    icon: 'facebook',
    color: '#1877f2',
    defaultLabel: 'Sign in with Facebook',
  },
  email: {
    icon: 'envelope',
    iconType: 'fal',
    color: '#000000',
    defaultLabel: 'Sign in with Email',
  },
};

export interface ButtonSocialProps {
  className?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
  isInline?: boolean;
  isFullWidth?: boolean;
  onPress?: () => void;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel: string;
  variant?: Social;
  to?: string;
}

ButtonSocial.componentName = 'ButtonSocial';

export default ButtonSocial;
